import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { pushState, pushSupported } from './push'

const DISMISS_KEY = 'chow:order-alerts-nudge-dismissed'

/**
 * A one-line prompt on the chef dashboard to turn on device notifications.
 *
 * The new-order bell only rings while ChooseChow is open. Push is what reaches
 * a chef whose phone is locked, and it was only reachable three taps deep under
 * Profile, so most kitchens never turned it on. Hidden once enabled, where the
 * browser cannot do push, or after the chef dismisses it on this device.
 */
export function OrderAlertsNudge({ profileId }: { profileId: string }) {
  const navigate = useNavigate()
  const [show, setShow] = useState(false)
  useEffect(() => {
    let current = true
    let dismissed = false
    try { dismissed = localStorage.getItem(DISMISS_KEY) === '1' } catch { /* show it */ }
    if (dismissed || !profileId || !pushSupported() || Notification.permission === 'denied') return
    void pushState(profileId).then((on) => { if (current) setShow(!on) }).catch(() => {})
    return () => { current = false }
  }, [profileId])
  if (!show) return null
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* hide for this visit */ }
    setShow(false)
  }
  return (
    <MinimalAlert
      tone="info"
      title="Never miss a new order"
      action={<>
        <MinimalButton size="sm" onClick={() => navigate('/notifications')}>Turn on alerts</MinimalButton>
        <MinimalButton size="sm" variant="ghost" onClick={dismiss}>Not now</MinimalButton>
      </>}
    >
      Get a notification on this device the moment a customer pays, even when ChooseChow is closed.
    </MinimalAlert>
  )
}
